import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchClassById, fetchClassLessons } from '../../api/classes';
import { useAuth } from '../../contexts/AuthContext';
import styles from './PrintSummaryPage.module.css';

const parseJsonField = (field) => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  try {
    const parsed = JSON.parse(field);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [field];
  }
};

const PrintSummaryPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [cls, setCls] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLessons, setExpandedLessons] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [classRes, lessonsRes] = await Promise.all([
          fetchClassById(id),
          fetchClassLessons(id),
        ]);
        setCls(classRes.data);
        setLessons(lessonsRes.data || []);
      } catch {
        setError('Failed to load class data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Group lessons by unit
  const { unitGroups, unassigned } = useMemo(() => {
    if (!cls) return { unitGroups: [], unassigned: [] };
    const byUnit = {};
    const noUnit = [];
    lessons.forEach(l => {
      if (l.unitId) {
        if (!byUnit[l.unitId]) byUnit[l.unitId] = [];
        byUnit[l.unitId].push(l);
      } else {
        noUnit.push(l);
      }
    });
    // Build unit groups in sequence order
    const groups = (cls.units || []).map(u => ({
      unit: u,
      lessons: byUnit[u.id] || [],
    })).filter(g => g.lessons.length > 0);
    return { unitGroups: groups, unassigned: noUnit };
  }, [cls, lessons]);

  const toggleLesson = (lessonId) => {
    setExpandedLessons(prev => ({ ...prev, [lessonId]: !prev[lessonId] }));
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedLessons({});
    } else {
      const all = {};
      lessons.forEach(l => { all[l.id] = true; });
      setExpandedLessons(all);
    }
    setAllExpanded(!allExpanded);
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatDateRange = (s, e) => `${formatDate(s)} – ${formatDate(e)}`;

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading summary...</p>
        </div>
      </div>
    );
  }

  if (error || !cls) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>{error || 'Class not found.'}</div>
        <Link to="/classes">Back to My Classes</Link>
      </div>
    );
  }

  const renderLessonRow = (lesson) => {
    const objectives = parseJsonField(lesson.objectives);
    const materials = parseJsonField(lesson.materials);
    const procedure = parseJsonField(lesson.procedure);
    const standards = lesson.lessonStandards || [];
    const isExpanded = expandedLessons[lesson.id];

    return (
      <div key={lesson.id} className={styles.lessonBlock}>
        {/* Condensed row */}
        <div className={styles.lessonRow}>
          <div className={styles.lessonTitle}>{lesson.title}</div>
          <div className={styles.lessonDate}>
            {lesson.scheduledDate ? formatDate(lesson.scheduledDate) : 'Draft'}
          </div>
          <div className={styles.lessonDuration}>
            {lesson.duration ? `${lesson.duration} min` : '—'}
          </div>
          <div className={styles.lessonObjectives}>
            {objectives.slice(0, 2).join('; ') || '—'}
            {objectives.length > 2 && ` (+${objectives.length - 2} more)`}
          </div>
          <div className={styles.lessonStandards}>
            {standards.length > 0
              ? standards.map(ls => ls.standard.standardCode).join(', ')
              : '—'
            }
          </div>
          <button className={styles.toggleBtn} onClick={() => toggleLesson(lesson.id)}>
            {isExpanded ? 'Hide Detail' : 'Show Full Detail'}
          </button>
        </div>

        {/* Expanded detail */}
        {isExpanded && (
          <div className={styles.expandedDetail}>
            {objectives.length > 0 && (
              <div className={styles.detailSection}>
                <h4>Objectives</h4>
                <ul>{objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
              </div>
            )}
            {materials.length > 0 && (
              <div className={styles.detailSection}>
                <h4>Materials</h4>
                <ul>{materials.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            )}
            {procedure.length > 0 && (
              <div className={styles.detailSection}>
                <h4>Procedure</h4>
                <ol>{procedure.map((p, i) => <li key={i}>{p}</li>)}</ol>
              </div>
            )}
            {lesson.assessment && (
              <div className={styles.detailSection}>
                <h4>Assessment</h4>
                <p>{lesson.assessment}</p>
              </div>
            )}
            {lesson.differentiation && (
              <div className={styles.detailSection}>
                <h4>Differentiation</h4>
                <p>{lesson.differentiation}</p>
              </div>
            )}
            {lesson.notes && (
              <div className={styles.detailSection}>
                <h4>Notes</h4>
                <p>{lesson.notes}</p>
              </div>
            )}
            {standards.length > 0 && (
              <div className={styles.detailSection}>
                <h4>Standards</h4>
                <ul>
                  {standards.map(ls => (
                    <li key={ls.id}>
                      <strong>{ls.standard.standardCode}</strong>: {ls.standard.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderUnitSection = ({ unit, lessons: unitLessons }) => (
    <div key={unit.id} className={styles.unitSection}>
      <div className={styles.unitHeader}>
        <h2>Unit {unit.sequenceOrder}: {unit.title}</h2>
        {unit.startDate && unit.endDate && (
          <span className={styles.unitDateRange}>{formatDateRange(unit.startDate, unit.endDate)}</span>
        )}
      </div>
      <div className={styles.lessonGrid}>
        <div className={styles.lessonGridHeader}>
          <div>Title</div>
          <div>Date</div>
          <div>Duration</div>
          <div>Objectives</div>
          <div>Standards</div>
          <div className={styles.toggleCol}></div>
        </div>
        {unitLessons.map(renderLessonRow)}
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Toolbar — hidden in print */}
      <div className={styles.toolbar}>
        <Link to={`/classes/${id}`} className={styles.toolbarLink}>Back to Class</Link>
        <div className={styles.toolbarActions}>
          <button className={styles.toolbarBtn} onClick={toggleAll}>
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </button>
          <button className={styles.toolbarBtn} onClick={() => window.print()}>
            Print / Export PDF
          </button>
        </div>
      </div>

      {/* Printable document */}
      <div className={styles.document}>
        {/* Cover page */}
        <div className={styles.coverPage}>
          <div className={styles.coverContent}>
            <div className={styles.coverRule} />
            <h1 className={styles.coverTitle}>{cls.name}</h1>
            <p className={styles.coverSubject}>{cls.subject}</p>
            <div className={styles.coverRule} />
            <div className={styles.coverDetails}>
              <p>{cls.gradeLevel}{cls.state ? ` \u00b7 ${cls.state}` : ''}</p>
              <p>{formatDateRange(cls.yearStart, cls.yearEnd)}</p>
              {user && <p className={styles.coverTeacher}>{user.name || user.email}</p>}
            </div>
          </div>
          <div className={styles.coverFootnote}>
            {lessons.length} lesson{lessons.length !== 1 ? 's' : ''} across {unitGroups.length} unit{unitGroups.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Unit sections */}
        {unitGroups.map(renderUnitSection)}

        {/* Unassigned lessons */}
        {unassigned.length > 0 && (
          <div className={styles.unitSection}>
            <div className={styles.unitHeader}>
              <h2>Unassigned Lessons</h2>
            </div>
            <div className={styles.lessonGrid}>
              <div className={styles.lessonGridHeader}>
                <div>Title</div>
                <div>Date</div>
                <div>Duration</div>
                <div>Objectives</div>
                <div>Standards</div>
                <div className={styles.toggleCol}></div>
              </div>
              {unassigned.map(renderLessonRow)}
            </div>
          </div>
        )}

        {lessons.length === 0 && (
          <p className={styles.noLessons}>No lessons have been created for this class yet.</p>
        )}

        {/* Footer */}
        <div className={styles.docFooter}>
          Generated on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
    </div>
  );
};

export default PrintSummaryPage;
